/**
 * GlobalProductPicker - 商品预设快速选择器
 * 用于快速搜索和选择商品预设，一键添加到用户工具箱
 *
 * @feature global-product-preset
 */
import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Search,
  Package,
  Plus,
  Loader2,
  Star,
  ChevronDown,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useDebounce } from "@/hooks/useDebounce";
import { toast } from "sonner";
import { toolboxService } from "@/services/toolboxService";
import type {
  GlobalProductSearchResult,
  MixedSearchResult,
  GlobalProductMarket,
} from "@/services/toolboxService";
import { ComplianceDot } from "@/components/shared/ComplianceBadge";

// ==================== 类型定义 ====================

interface GlobalProductPickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onProductAdded?: () => void;
  className?: string;
}

// ==================== 辅助组件 ====================

/** 商品卡片组件 */
function ProductCard({
  product,
  onAdd,
  isAdding,
}: {
  product: GlobalProductSearchResult;
  onAdd: () => void;
  isAdding: boolean;
}) {
  const { t } = useTranslation("common");

  return (
    <div
      className={cn(
        "group relative flex items-center gap-3 p-3 rounded-lg border border-border/60",
        "bg-card hover:bg-accent/50 hover:border-accent transition-all duration-200",
      )}
    >
      {/* 缩略图 */}
      <div className="flex-shrink-0 w-12 h-12 rounded-md bg-muted flex items-center justify-center overflow-hidden">
        {product.thumbnail_url ? (
          <img
            src={product.thumbnail_url}
            alt={product.name}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <Package className="h-6 w-6 text-muted-foreground" />
        )}
      </div>

      {/* 商品信息 */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h4 className="text-sm font-medium text-foreground truncate">
            {product.name || `${product.brand} ${product.model}`}
          </h4>
          {product.regulatory_tier && (
            <ComplianceDot tier={product.regulatory_tier} />
          )}
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-xs text-muted-foreground">{product.brand}</span>
          <span className="text-xs text-muted-foreground/50">·</span>
          <span className="text-xs text-muted-foreground">{product.model}</span>
        </div>
        {/* 使用统计 - 暂时隐藏 */}
        {/* {product.usage_count > 0 && (
          <div className="flex items-center gap-1 mt-1">
            <Star className="h-3 w-3 text-amber-500" />
            <span className="text-xs text-muted-foreground">
              {t("toolbox.product.usageCount", "{{count}} 人使用", {
                count: product.usage_count,
              })}
            </span>
          </div>
        )} */}
      </div>

      {/* 添加按钮 */}
      <Button
        size="sm"
        variant="ghost"
        className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
        onClick={onAdd}
        disabled={isAdding}
      >
        {isAdding ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Plus className="h-4 w-4" />
        )}
      </Button>
    </div>
  );
}

/** 模板卡片组件（带展开表单） */
function TemplateCard({
  template,
  searchQuery,
  onAdd,
  isAdding,
}: {
  template: {
    id: number;
    name: string;
    icon?: string;
    category_slug: string;
    keywords?: Record<string, string[]>;
  };
  searchQuery?: string;
  onAdd: (data: {
    name: string;
    // ... rest of props same
    toolbox_template_id: number;
    item_type: string;
    concentration_ratio?: number;
    expire_at?: string;
    opened_at?: string;
  }) => void;
  isAdding: boolean;
}) {
  const { t } = useTranslation("common");
  const [expanded, setExpanded] = useState(false);
  const [formData, setFormData] = useState({
    name: template.name,
    concentration_ratio: "1.0",
    expire_at: "",
    opened_at: "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onAdd({
      name: formData.name || template.name,
      toolbox_template_id: template.id,
      item_type: template.category_slug,
      concentration_ratio: parseFloat(formData.concentration_ratio) || 1.0,
      expire_at: formData.expire_at || undefined,
      opened_at: formData.opened_at || undefined,
    });
  };

  // 提取匹配的关键词
  const matchedKeywords = React.useMemo(() => {
    if (!searchQuery || !template.keywords) return [];
    const query = searchQuery.toLowerCase();
    const allKeywords = Object.values(template.keywords).flat();
    return allKeywords.filter((kw) => kw.toLowerCase().includes(query));
  }, [searchQuery, template.keywords]);

  return (
    <div
      className={cn(
        "rounded-lg border border-border/60 transition-all duration-200",
        expanded ? "bg-muted/30" : "bg-card hover:bg-accent/30",
      )}
    >
      {/* 模板头部 */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 p-3 text-left"
      >
        {/* 图标 */}
        <div className="flex-shrink-0 w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center">
          <span className="text-lg">{template.icon || "📦"}</span>
        </div>
        {/* 信息 */}
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground truncate">
            {template.name}
          </h4>
          {matchedKeywords.length > 0 ? (
            <div className="flex items-center gap-1 text-[10px] text-muted-foreground font-medium">
              <Search className="h-2.5 w-2.5" />
              <span className="truncate">{matchedKeywords.join(", ")}</span>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">
              {t("toolbox.template.clickToCustomize", "点击填写详情")}
            </p>
          )}
        </div>
        {/* 展开指示 */}
        <ChevronDown
          className={cn(
            "h-4 w-4 text-muted-foreground transition-transform",
            expanded && "rotate-180",
          )}
        />
      </button>

      {/* 展开的表单 */}
      {expanded && (
        <form onSubmit={handleSubmit} className="px-3 pb-3 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div className="col-span-2">
              <Input
                placeholder={t("toolbox.form.namePlaceholder", "名称")}
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, name: e.target.value }))
                }
                className="h-9 text-sm"
              />
            </div>
            <div>
              <Input
                type="number"
                step="0.1"
                min="0.1"
                placeholder={t("toolbox.form.concentrationRatio", "浓度倍率")}
                value={formData.concentration_ratio}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    concentration_ratio: e.target.value,
                  }))
                }
                className="h-9 text-sm"
              />
              <p className="text-[10px] text-muted-foreground mt-0.5">
                {t("toolbox.form.concentrationHint", "浓缩液填倍数")}
              </p>
            </div>
            <div>
              <Input
                type="date"
                placeholder={t("toolbox.form.expireAt", "有效期")}
                value={formData.expire_at}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    expire_at: e.target.value,
                  }))
                }
                className="h-9 text-sm"
              />
            </div>
          </div>
          <Button
            type="submit"
            size="sm"
            className="w-full h-9"
            disabled={isAdding}
          >
            {isAdding ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              t("toolbox.form.addToToolbox", "添加到工具箱")
            )}
          </Button>
        </form>
      )}
    </div>
  );
}

/** 市场标签 */
function MarketBadge({ market }: { market: GlobalProductMarket }) {
  const labels: Record<GlobalProductMarket, string> = {
    global: "🌍",
    cn: "🇨🇳",
    us: "🇺🇸",
    jp: "🇯🇵",
    eu: "🇪🇺",
  };
  return (
    <Badge variant="outline" className="text-xs px-1.5 py-0">
      {labels[market]}
    </Badge>
  );
}

// ==================== 主组件 ====================

export function GlobalProductPicker({
  open,
  onOpenChange,
  onProductAdded,
  className,
}: GlobalProductPickerProps) {
  const { t } = useTranslation("common");
  const queryClient = useQueryClient();

  // 搜索状态
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<MixedSearchResult | null>(
    null,
  );
  const [isSearching, setIsSearching] = useState(false);
  const [addingProductId, setAddingProductId] = useState<number | null>(null);
  const [addingTemplateId, setAddingTemplateId] = useState<number | null>(null);

  // 获取推荐内容（用于未搜索时展示）
  const { data: recommendations } = useQuery({
    queryKey: ["toolbox-recommendations"],
    queryFn: () => toolboxService.mixedSearch(""),
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  // 防抖搜索
  const debouncedSearch = useDebounce(searchQuery, 300);

  // 执行搜索
  const handleSearch = useCallback(async () => {
    if (!debouncedSearch.trim()) {
      setSearchResults(null);
      return;
    }

    setIsSearching(true);
    try {
      const results = await toolboxService.mixedSearch(debouncedSearch, {
        limit: 20,
      });
      setSearchResults(results);
    } catch (error) {
      console.error("Search failed:", error);
      toast.error(t("toolbox.product.searchError", "搜索失败，请重试"));
    } finally {
      setIsSearching(false);
    }
  }, [debouncedSearch, t]);

  // 监听防抖搜索变化
  React.useEffect(() => {
    handleSearch();
  }, [handleSearch]);

  // 添加商品到工具箱
  const addMutation = useMutation({
    mutationFn: async (product: GlobalProductSearchResult) => {
      return toolboxService.createItemFromProduct({
        source_type: "product",
        source_id: product.id,
      });
    },
    onMutate: (product) => {
      setAddingProductId(product.id);
    },
    onSuccess: () => {
      toast.success(t("toolbox.product.addSuccess", "已添加到工具箱"));
      queryClient.invalidateQueries({ queryKey: ["toolbox"] });
      queryClient.invalidateQueries({
        queryKey: ["user-toolbox-for-assessment"],
      });
      onProductAdded?.();
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.errors?.[0] ||
        t("toolbox.product.addError", "添加失败，请重试");
      toast.error(message);
    },
    onSettled: () => {
      setAddingProductId(null);
    },
  });

  // 添加模板物品到工具箱
  const templateMutation = useMutation({
    mutationFn: async (data: {
      name: string;
      toolbox_template_id: number;
      item_type: string;
      concentration_ratio?: number;
      expire_at?: string;
      opened_at?: string;
    }) => {
      return toolboxService.createItem({
        name: data.name,
        item_type: data.item_type,
        toolbox_template_id: data.toolbox_template_id,
        concentration_ratio: data.concentration_ratio,
        expire_at: data.expire_at,
        opened_at: data.opened_at,
      });
    },
    onMutate: (data) => {
      setAddingTemplateId(data.toolbox_template_id);
    },
    onSuccess: () => {
      toast.success(t("toolbox.template.addSuccess", "已添加到工具箱"));
      queryClient.invalidateQueries({ queryKey: ["toolbox"] });
      queryClient.invalidateQueries({
        queryKey: ["user-toolbox-for-assessment"],
      });
      onProductAdded?.();
    },
    onError: (error: any) => {
      const message =
        error?.details?.base?.[0] ||
        error?.message ||
        t("toolbox.template.addError", "添加失败");
      toast.error(message);
    },
    onSettled: () => {
      setAddingTemplateId(null);
    },
  });

  // 处理添加商品
  const handleAddProduct = (product: GlobalProductSearchResult) => {
    addMutation.mutate(product);
  };

  // 处理添加模板物品
  const handleAddTemplate = (data: {
    name: string;
    toolbox_template_id: number;
    item_type: string;
    concentration_ratio?: number;
    expire_at?: string;
    opened_at?: string;
  }) => {
    templateMutation.mutate(data);
  };

  // 重置状态
  const handleClose = () => {
    setSearchQuery("");
    setSearchResults(null);
    onOpenChange(false);
  };

  // 是否有搜索结果
  const hasResults =
    searchResults &&
    (searchResults.products.length > 0 || searchResults.templates.length > 0);
  const hasProducts = searchResults && searchResults.products.length > 0;
  const hasTemplates = searchResults && searchResults.templates.length > 0;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className={cn("sm:max-w-lg", className)}>
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            {t("toolbox.product.pickerTitle", "添加商品")}
          </DialogTitle>
        </DialogHeader>

        {/* 搜索框 */}
        <div className="relative mt-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t("toolbox.product.searchPlaceholder", "搜索商品...")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 h-11"
            autoFocus
          />
          {isSearching && (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
          )}
        </div>

        {/* 搜索结果 */}
        <ScrollArea className="h-[320px] mt-2 -mx-2 px-2">
          {!searchQuery && (
            <div className="flex flex-col h-full py-4">
              <div className="flex flex-col items-center justify-center text-center mb-8">
                <Package className="h-10 w-10 text-muted-foreground/20 mb-3" />
                <p className="text-xs text-muted-foreground">
                  {t("toolbox.product.searchHint", "搜索药品、器材...")}
                </p>
              </div>

              {/* 推荐内容 */}
              {!recommendations ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground/30" />
                </div>
              ) : recommendations.products.length > 0 ? (
                <div className="space-y-4 px-1">
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                    <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500" />
                    {t("toolbox.product.recommendationsTitle", "推荐添加")}
                  </h3>
                  <div className="grid grid-cols-1 gap-2">
                    {recommendations.products.map((product) => (
                      <ProductCard
                        key={product.id}
                        product={product}
                        onAdd={() => handleAddProduct(product)}
                        isAdding={addingProductId === product.id}
                      />
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          )}

          {searchQuery && !isSearching && !hasResults && (
            <div className="flex flex-col items-center justify-center h-full text-center py-8">
              <Package className="h-12 w-12 text-muted-foreground/30 mb-4" />
              <p className="text-sm text-muted-foreground">
                {t("toolbox.product.noResults", "未找到匹配的商品")}
              </p>
              <p className="text-xs text-muted-foreground/70 mt-1">
                {t("toolbox.product.tryOtherKeywords", "尝试其他关键词")}
              </p>
            </div>
          )}

          {hasProducts && (
            <div className="space-y-2">
              <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                {t("toolbox.product.productsSection", "商品预设")}
              </h3>
              {searchResults?.products.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  onAdd={() => handleAddProduct(product)}
                  isAdding={addingProductId === product.id}
                />
              ))}
            </div>
          )}

          {/* 模板结果（自定义添加） */}
          {hasTemplates && (
            <div className={cn("space-y-2", hasProducts && "mt-4")}>
              <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                {t("toolbox.template.customSection", "自定义添加")}
              </h3>
              {searchResults?.templates.map((template) => (
                <TemplateCard
                  key={template.id}
                  template={{
                    id: template.id,
                    name: template.name,
                    icon: template.icon,
                    category_slug: template.category_slug,
                    keywords: template.keywords,
                  }}
                  searchQuery={searchQuery}
                  onAdd={handleAddTemplate}
                  isAdding={addingTemplateId === template.id}
                />
              ))}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

export default GlobalProductPicker;
